/**
 * Wraps the authed routes with an idle-session monitor.
 *
 * 30 minutes of zero pointer/keyboard/touch activity → modal opens
 * with a 60-second visible countdown. If the operator clicks
 * "Stay signed in" the timer resets; otherwise the auth store is
 * wiped (matching the server-side logout endpoint's effect — we
 * fire-and-forget the POST so a network blip doesn't trap the
 * operator in a "logged-out-but-still-authenticated" limbo).
 *
 * Idle / grace durations are env-tunable via `VITE_IDLE_MS` and
 * `VITE_IDLE_GRACE_MS` so QA can dial them down for testing without
 * a code change.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useIdleTimeout } from '@/hooks/use-idle-timeout';
import { apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth';

const DEFAULT_IDLE_MS = 30 * 60 * 1000;
const DEFAULT_GRACE_MS = 60 * 1000;

function readMs(envValue: string | undefined, fallback: number): number {
  const parsed = Number(envValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

interface IdleTimeoutGuardProps {
  children: React.ReactNode;
}

export function IdleTimeoutGuard({ children }: IdleTimeoutGuardProps) {
  const navigate = useNavigate();
  const isSignedIn = useAuthStore((s) => Boolean(s.accessToken));
  const logoutStore = useAuthStore((s) => s.logout);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const idleMs = readMs(import.meta.env.VITE_IDLE_MS, DEFAULT_IDLE_MS);
  const graceMs = readMs(import.meta.env.VITE_IDLE_GRACE_MS, DEFAULT_GRACE_MS);

  const { reset } = useIdleTimeout({
    enabled: isSignedIn,
    idleMs,
    graceMs,
    onIdle: () => {
      setSecondsLeft(Math.round(graceMs / 1000));
      setShowWarning(true);
    },
    onTimeout: () => {
      setShowWarning(false);
      // Fire-and-forget — even if the request fails, the client-side
      // clear must happen so the operator isn't left holding a token
      // they think is dead.
      apiClient.post('/auth/logout').catch(() => undefined);
      logoutStore();
      navigate('/login', { replace: true });
    },
  });

  // Live countdown while the warning is open.
  useEffect(() => {
    if (!showWarning) return;
    const tick = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [showWarning]);

  function handleStay() {
    setShowWarning(false);
    reset();
  }

  function handleLogoutNow() {
    setShowWarning(false);
    apiClient.post('/auth/logout').catch(() => undefined);
    logoutStore();
    navigate('/login', { replace: true });
  }

  return (
    <>
      {children}
      <AlertDialog open={showWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>You&apos;ve been idle</AlertDialogTitle>
            <AlertDialogDescription>
              For your security you&apos;ll be signed out in <strong>{secondsLeft}</strong>{' '}
              second{secondsLeft === 1 ? '' : 's'} if there&apos;s no activity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleLogoutNow}>Sign out now</AlertDialogCancel>
            <AlertDialogAction onClick={handleStay}>Stay signed in</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
